import * as B from '@blueprintjs/core';
import type {IconName} from '@blueprintjs/icons';
import React from 'react';

import * as mapUtils from 'app/utils/mapUtils';

import cs from './AgentPromptInput.module.scss';
import {getUnsupportedScenesMessage, imageRefsAreSupported} from '../PromptView/promptConfigs';

export interface PromptOption {
  label: string;
  icon: IconName;
  renderControl: () => React.ReactNode;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (text: string) => void;
  options?: PromptOption[];
  // When omitted, the prompt isn't tied to map scenes and the scene-support gate is skipped.
  imageRefs?: mapUtils.MapImageRefs;
  loading?: boolean;
  loadingMessage?: string;
  height?: string;
  placeholder: string;
}

const AgentPromptInput: React.FC<Props> = ({
  value,
  onChange,
  onSubmit,
  options,
  imageRefs,
  loading = false,
  loadingMessage,
  height = '40vh',
  placeholder,
}) => {
  const scenesSupported = !imageRefs || imageRefsAreSupported(imageRefs);

  const handleSubmit = () => {
    if (value.trim()) {
      onSubmit(value);
    }
  };

  return (
    <div className={cs.textareaWrapper}>
      <B.TextArea
        className={cs.promptInput}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={loading}
        style={{height}}
        fill
      />
      <div className={cs.textareaFooter}>
        {options && options.length > 0 && (
          <B.Popover
            placement="top"
            content={
              <div className={cs.settingsPopover}>
                {options.map((option) => (
                  <div key={option.label} className={cs.optionRow}>
                    <B.Icon icon={option.icon} size={14} className={cs.optionIcon} />
                    <span className={cs.optionLabel}>{option.label}</span>
                    {option.renderControl()}
                  </div>
                ))}
              </div>
            }
          >
            <B.Button icon="settings" disabled={loading} minimal small />
          </B.Popover>
        )}
        {loading && loadingMessage && <span className={cs.loadingMessage}>{loadingMessage}</span>}
        <B.Tooltip
          disabled={scenesSupported && !!value.trim() && !loading}
          content={
            (imageRefs && !scenesSupported ? `${getUnsupportedScenesMessage(imageRefs)} ` : '') +
            (!value.trim() ? 'Please enter a prompt.' : '')
          }
        >
          <B.Button
            className={cs.submitButton}
            icon="send-message"
            minimal
            loading={loading}
            disabled={!value.trim() || !scenesSupported || loading}
            onClick={handleSubmit}
          />
        </B.Tooltip>
      </div>
    </div>
  );
};

export default AgentPromptInput;
